import { useEffect, useState } from "react";
import {
  pictureOptions,
  POSTER_PROMPT_MAX_CHARS,
  POSTER_REGEN_LIMIT,
  POSTER_SHAPE_ASPECT,
  POSTER_SHAPE_LABEL,
  POSTER_SHAPES,
  POSTER_SLOT_NAME,
  POSTER_SLOTS,
  type GenerationErrorCode,
  type PosterCutView,
  type PosterImportCandidate,
  type PosterShape,
  type PosterSlot,
  type PosterStyleKey,
} from "@studio/shared";
import { InlineSpinner } from "../../components/InlineSpinner";
import { ErrorPage, InlineError, LoadingPage, StudioShell } from "../../components/StudioShell";
import { ApiError, apiRequest } from "../../lib/api";
import { navigate } from "../../lib/navigation";
import { pathForStep } from "../../lib/session";
import { importPicture, listPosterImports, requestSlotImage, usePoster } from "../../poster/api";
import { goPosterStep } from "../../poster/steps";
import { PosterResumeGate } from "./PosterResumeGate";

/**
 * 포스터 1단계 「그림 만들기」 — 목업 s1(mockups/poster-plan.html).
 *
 * 🔑 칸 1개로 시작해 「그림 더 만들기」로 최대 3장(2026-10-04). 칸마다 모양(세로·가로·정사각)을 고른다.
 *    다시 그리기는 «모든 그림이 합쳐서» 센다(D2). 칸별 첫 그림은 횟수를 안 쓴다.
 *    한 번에 한 칸씩 그린다(서버 016 — 학생당 그림 순차).
 * 🔑 「더 만들기」로 연 빈 칸은 이 화면에만 있다 — 글을 쓰고 그려야 서버에 칸이 생긴다.
 * 🔑 글은 학생이 자유롭게 쓴다(300자). 도움말 칩은 누르면 글 끝에 이어 붙고, 느낌 6종은 고르고 싶을 때만.
 */

const SLOT_TITLE = POSTER_SLOT_NAME;

/** 서버에 흔적이 있는 칸(그림·그리는 중·글) — 새로고침해도 보여야 한다 */
function cutInUse(cut: PosterCutView): boolean {
  return Boolean(cut.image_url || cut.active_job || cut.text);
}

function ShapeIcon({ shape }: { shape: PosterShape }) {
  const [w, h] = shape === "portrait" ? [12, 16] : shape === "landscape" ? [16, 12] : [14, 14];
  return (
    <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
      <rect x={(20 - w) / 2} y={(20 - h) / 2} width={w} height={h} rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}
const HELPERS = ["밤하늘 아래", "로봇 친구와 함께", "전시장 벽에 걸린 그림들", "따뜻하고 포근한 분위기", "위에서 내려다본 모습"];

const failureMessages: Record<GenerationErrorCode, string> = {
  PROVIDER_TIMEOUT: "시간이 오래 걸려 멈췄어요. 다시 그려 주세요.",
  PROVIDER_REJECTED: "그림을 그리지 못했어요. 글을 조금 바꿔서 다시 그려 주세요.",
  MODERATION_BLOCKED: "AI 가 그리기 어려운 내용이에요. 글을 바꿔서 다시 그려 주세요.",
  BUDGET_EXHAUSTED: "오늘 studio 가 그릴 수 있는 그림을 다 썼어요. 선생님께 알려 주세요.",
  INTERNAL: "그림을 그리지 못했어요. 다시 그려 주세요.",
};

function requestError(caught: unknown): string {
  if (!(caught instanceof ApiError)) return "요청을 처리하지 못했어요. 다시 눌러 주세요.";
  if (caught.code === "LIMIT_EXHAUSTED") return "다시 그리기 횟수를 다 썼어요. 선생님께 말씀드려 보세요.";
  if (caught.code === "IMAGE_ACTIVE" || caught.code === "CUT_BUSY") return "다른 칸 그림을 그리고 있어요. 다 그리면 이어서 그릴 수 있어요.";
  return caught.message;
}

function SlotCard({
  cut,
  shape,
  selected,
  onSelect,
}: {
  cut: PosterCutView;
  /** 그림이 있으면 그 그림 모양, 없으면 고른 모양 */
  shape: PosterShape;
  selected: boolean;
  onSelect: () => void;
}) {
  const busy = Boolean(cut.active_job);
  const label = `${SLOT_TITLE[cut.slot]} · ${POSTER_SHAPE_LABEL[shape]}`;
  return (
    <button
      type="button"
      className={`poster-slot${selected ? " is-selected" : ""}${busy ? " is-loading" : ""}${!cut.image_url && !busy ? " is-empty" : ""}`}
      style={{ aspectRatio: String(POSTER_SHAPE_ASPECT[shape]) }}
      aria-pressed={selected}
      aria-label={`${label}${busy ? " · 그리는 중" : cut.image_url ? "" : " · 비어 있음"}`}
      onClick={onSelect}
    >
      {cut.image_url ? <img src={cut.image_url} alt="" /> : null}
      <span className="poster-slot-label">{label}</span>
      {busy ? (
        <span className="poster-slot-wait">
          <span className="poster-spin" aria-hidden="true" />
          그리는 중이에요
          <small>보통 20~40초</small>
        </span>
      ) : null}
      {!busy && !cut.image_url ? <span className="poster-slot-empty">+ 그림 만들기</span> : null}
    </button>
  );
}

export function PosterDrawPage({ workId }: { workId: string }) {
  const poster = usePoster(workId);
  const [slot, setSlot] = useState<PosterSlot>(1);
  const [texts, setTexts] = useState<Partial<Record<PosterSlot, string>>>({});
  const [styles, setStyles] = useState<Partial<Record<PosterSlot, PosterStyleKey | null>>>({});
  const [shapes, setShapes] = useState<Partial<Record<PosterSlot, PosterShape>>>({});
  /** 「그림 더 만들기」로 연 칸(아직 서버에 흔적이 없는 칸) */
  const [opened, setOpened] = useState<PosterSlot[]>([]);
  const [busy, setBusy] = useState<"draw" | "next" | "import" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imports, setImports] = useState<PosterImportCandidate[] | null>(null);

  // 서버에 적힌 글·느낌을 처음 한 번 채운다(이어하기). 학생이 고치는 중인 칸은 덮지 않는다.
  useEffect(() => {
    if (!poster.data) return;
    setTexts((current) => {
      const next = { ...current };
      for (const cut of poster.data!.cuts) if (next[cut.slot] === undefined && cut.text) next[cut.slot] = cut.text;
      return next;
    });
    setStyles((current) => {
      const next = { ...current };
      for (const cut of poster.data!.cuts) if (next[cut.slot] === undefined && cut.style) next[cut.slot] = cut.style;
      return next;
    });
  }, [poster.data]);

  if (poster.loading) return <LoadingPage label="포스터를 불러오는 중..." />;
  if (!poster.data) return <ErrorPage message={poster.error?.message ?? "포스터를 불러오지 못했어요."} />;
  const data = poster.data;
  const cut = data.cuts.find((candidate) => candidate.slot === slot)!;
  const text = texts[slot] ?? "";
  const style = styles[slot] ?? null;
  const shapeOf = (candidate: PosterCutView): PosterShape => shapes[candidate.slot] ?? candidate.shape;
  const shape = shapeOf(cut);
  // 보이는 칸 = 1번 + 서버에 흔적이 있는 칸 + 이 화면에서 연 칸
  const visible = data.cuts.filter((candidate) => candidate.slot === 1 || cutInUse(candidate) || opened.includes(candidate.slot));
  const nextSlot = POSTER_SLOTS.find((candidate) => !visible.some((shown) => shown.slot === candidate));
  const anyActive = data.cuts.some((candidate) => candidate.active_job);
  // 선물을 받으면 남은 횟수가 기본 한도보다 클 수 있다 — 그땐 칸을 다 비워 보인다(음수가 되지 않게).
  const regenUsed = Math.max(0, POSTER_REGEN_LIMIT - data.image_regen_remaining);
  const isRedraw = Boolean(cut.image_url);
  const exhausted = isRedraw && data.image_regen_remaining <= 0;
  const drawnCount = data.cuts.filter((candidate) => candidate.image_url).length;
  const drawDisabled = busy !== null || anyActive || exhausted;

  const draw = async (): Promise<void> => {
    if (!text.trim()) {
      setError("어떤 그림을 그릴지 글로 써 주세요.");
      return;
    }
    setBusy("draw");
    setError(null);
    try {
      await requestSlotImage(workId, slot, { text: text.trim(), style: style ?? undefined, shape });
      await poster.refresh();
    } catch (caught) {
      setError(requestError(caught));
    } finally {
      setBusy(null);
    }
  };

  const goEdit = async (): Promise<void> => {
    setBusy("next");
    setError(null);
    try {
      await apiRequest(`/api/works/${encodeURIComponent(workId)}/autosave`, {
        method: "POST",
        body: JSON.stringify({ step: "pst_edit", gifts_v: 2 }),
      });
      navigate(pathForStep(workId, "pst_edit"));
    } catch (caught) {
      setError(requestError(caught));
      setBusy(null);
    }
  };

  const openImports = async (): Promise<void> => {
    setBusy("import");
    setError(null);
    try {
      setImports((await listPosterImports(workId)).items);
    } catch (caught) {
      setError(requestError(caught));
    } finally {
      setBusy(null);
    }
  };

  const pickImport = async (item: PosterImportCandidate): Promise<void> => {
    setBusy("import");
    setError(null);
    try {
      await importPicture(workId, item.work_id, slot);
      setImports(null);
      await poster.refresh();
    } catch (caught) {
      setError(requestError(caught));
    } finally {
      setBusy(null);
    }
  };

  return (
    <PosterResumeGate state={data}>
    <StudioShell
      currentStep={1}
      wide
      imageRegenRemaining={data.image_regen_remaining}
      onStepClick={(step) => {
        if (step !== 1 && busy === null) void goPosterStep(workId, step).catch((caught: unknown) => setError(requestError(caught)));
      }}
    >
      <div className="poster-draw">
        <section className="poster-draw-left" aria-label="그림 칸">
          <header className="poster-draw-heading">
            <h1 tabIndex={-1}>포스터에 쓸 그림을 만들어요</h1>
            <p>그림을 눌러 고른 뒤, 오른쪽에서 정하세요. 「그림 더 만들기」로 3장까지 만들 수 있어요.</p>
          </header>
          <div className="poster-slots">
            {visible.map((candidate) => (
              <SlotCard
                key={candidate.slot}
                cut={candidate}
                shape={candidate.image_url ? candidate.shape : shapeOf(candidate)}
                selected={candidate.slot === slot}
                onSelect={() => { setSlot(candidate.slot); setError(null); }}
              />
            ))}
            {nextSlot ? (
              <button
                type="button"
                className="poster-slot poster-slot-add"
                onClick={() => {
                  setOpened((current) => [...current, nextSlot]);
                  setSlot(nextSlot);
                  setError(null);
                }}
              >
                <span aria-hidden="true">＋</span>
                그림 더 만들기
                <small>{visible.length}/{POSTER_SLOTS.length}장</small>
              </button>
            ) : null}
          </div>
          <div className="poster-draw-actions">
            <button className="button-secondary" type="button" disabled={busy !== null} onClick={() => void openImports()}>
              {busy === "import" && !imports ? <InlineSpinner label="찾는 중..." /> : "내 지난 그림 불러오기"}
            </button>
            <button className="button-primary" type="button" disabled={busy !== null || (drawnCount === 0 && !anyActive)} onClick={() => void goEdit()}>
              {busy === "next" ? <InlineSpinner label="이동 중..." /> : "편집하러 가기 →"}
            </button>
          </div>
          <p className="poster-draw-foot">
            그림 만들기 수업에서 만든 내 그림이 있으면 불러와 쓸 수 있어요. 불러오기는 그림 횟수를 쓰지 않아요.
            {drawnCount === 0 && !anyActive ? " 편집은 그림을 한 장 이상 만든 뒤에 할 수 있어요." : " 그림을 기다리는 동안에도 편집하러 가도 돼요."}
          </p>
        </section>

        <aside className="poster-draw-right" aria-label={`${SLOT_TITLE[slot]} 정하기`}>
          <div className="poster-panel-head">
            <h2>{SLOT_TITLE[slot]}</h2>
            <span className="poster-tag">고른 칸</span>
          </div>
          <p className="poster-field">그림 모양 {cut.image_url ? <small>(바꾸면 다시 그릴 때 그 모양으로)</small> : null}</p>
          <div className="poster-shapes" role="radiogroup" aria-label="그림 모양">
            {POSTER_SHAPES.map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={shape === option}
                className={`poster-shape${shape === option ? " is-on" : ""}`}
                onClick={() => setShapes({ ...shapes, [slot]: option })}
              >
                <ShapeIcon shape={option} />
                {POSTER_SHAPE_LABEL[option]}
              </button>
            ))}
          </div>

          <label className="poster-field" htmlFor="poster-prompt">
            <span>어떤 그림을 그릴까요? <small>(자유롭게 · {POSTER_PROMPT_MAX_CHARS}자)</small></span>
          </label>
          <textarea
            id="poster-prompt"
            className="poster-prompt"
            rows={5}
            value={text}
            maxLength={POSTER_PROMPT_MAX_CHARS}
            placeholder="예: 노트북 앞에 둘러앉은 친구들이 화면 속에서 튀어나온 음표를 보며 웃고 있어요."
            onChange={(event) => setTexts({ ...texts, [slot]: event.target.value })}
            onKeyDown={(event) => {
              // Ctrl/⌘+Enter = 그리기 버튼(2026-10-04 jery). 한글 조합 중 Enter 는 글자 확정이라 건너뛴다.
              if (event.key !== "Enter" || !(event.ctrlKey || event.metaKey) || event.nativeEvent.isComposing) return;
              event.preventDefault();
              if (!drawDisabled) void draw();
            }}
          />
          <div className="poster-prompt-meta">
            <span>누가 · 어디서 · 무엇을 · 어떤 분위기로 쓰면 더 잘 그려요</span>
            <span aria-live="polite">{[...text].length}/{POSTER_PROMPT_MAX_CHARS}</span>
          </div>

          <p className="poster-field">막히면 눌러 보세요 <small>(누르면 글에 이어 붙어요)</small></p>
          <div className="poster-chips">
            {HELPERS.map((helper) => (
              <button
                key={helper}
                type="button"
                className="poster-chip"
                onClick={() => {
                  const joined = text.trim() ? `${text.trim()} ${helper}` : helper;
                  setTexts({ ...texts, [slot]: [...joined].slice(0, POSTER_PROMPT_MAX_CHARS).join("") });
                }}
              >+ {helper}</button>
            ))}
          </div>

          <p className="poster-field">그림 느낌 <small>(선택 · 안 고르면 글에 쓴 대로)</small></p>
          <div className="poster-styles" role="radiogroup" aria-label="그림 느낌">
            {pictureOptions.style.map((option) => (
              <button
                key={option.key}
                type="button"
                role="radio"
                aria-checked={style === option.key}
                className={`poster-style${style === option.key ? " is-on" : ""}`}
                onClick={() => setStyles({ ...styles, [slot]: style === option.key ? null : option.key })}
              >
                <img src={`/picture-styles/${option.key}.webp`} alt="" />
                <span>{option.label}</span>
              </button>
            ))}
          </div>

          <p className="poster-privacy">
            이 글은 그림을 그리는 AI(구글)에게 전달돼요. <b>내 이름·학교·친구 이름은 쓰지 마세요.</b>
          </p>

          {/* 그리기 버튼은 패널 아래에 붙어 있다 — 글·칩·느낌이 길어도 스크롤 아래로 숨지 않는다(2026-10-04 jery). */}
          <div className="poster-draw-submit">
          <div className="poster-budget">
            <span>다시 그리기 <b>{data.image_regen_remaining}번</b> 남았어요 (모든 그림이 같이 씀)</span>
            <span className="poster-pips" aria-hidden="true">
              {Array.from({ length: POSTER_REGEN_LIMIT }, (_, index) => (
                <i key={index} className={index < regenUsed ? "is-used" : ""} />
              ))}
            </span>
          </div>
          {cut.failed_code && !cut.active_job ? <InlineError>{failureMessages[cut.failed_code]}</InlineError> : null}
          {error ? <InlineError>{error}</InlineError> : null}
          <button
            className="button-primary full-width"
            type="button"
            disabled={drawDisabled}
            aria-keyshortcuts="Control+Enter Meta+Enter"
            onClick={() => void draw()}
          >
            {busy === "draw"
              ? <InlineSpinner label="시작하는 중..." />
              : cut.active_job
                ? <InlineSpinner label="이 칸을 그리는 중..." />
                : anyActive
                  ? "다른 칸을 그리는 중이에요"
                  : isRedraw ? "이 칸 다시 그리기" : "이 칸 그림 그리기"}
          </button>
          {exhausted ? <p className="poster-budget-note">다시 그리기를 다 썼어요. 선생님이 횟수를 더 주실 수 있어요.</p> : null}
          {!isRedraw ? <p className="poster-budget-note">그림마다 첫 번째는 횟수를 쓰지 않아요.</p> : null}
          <p className="poster-budget-note poster-shortcut-hint">글 쓰는 칸에서 <kbd>Ctrl</kbd>+<kbd>Enter</kbd> (맥은 <kbd>⌘</kbd>+<kbd>Enter</kbd>)로도 그려요</p>
          </div>
        </aside>
      </div>

      {imports ? (
        <div className="exit-dialog-backdrop" onClick={(event) => { if (event.target === event.currentTarget) setImports(null); }}>
          <div className="exit-dialog poster-import-dialog" role="dialog" aria-modal="true" aria-labelledby="poster-import-title">
            <button className="button-ghost exit-dialog-close" type="button" onClick={() => setImports(null)}>
              <span aria-hidden="true">×</span>닫기
            </button>
            <h2 id="poster-import-title">내 지난 그림 불러오기</h2>
            <p>고른 칸({SLOT_TITLE[slot]})에 들어가요. 그림 횟수는 쓰지 않아요.</p>
            {imports.length === 0 ? (
              <p className="poster-import-empty">그림 만들기 수업에서 같은 이름으로 만든 그림이 없어요.</p>
            ) : (
              <div className="poster-import-grid">
                {imports.map((item) => (
                  <button key={item.work_id} type="button" className="poster-import-item" disabled={busy !== null} onClick={() => void pickImport(item)}>
                    <img src={item.url} alt="" />
                    <span>{item.session_title}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </StudioShell>
    </PosterResumeGate>
  );
}
